import { Component, forwardRef, Input, OnChanges, OnInit, SimpleChanges } from "@angular/core";
import {
  AbstractControl,
  ControlValueAccessor,
  FormArray,
  FormBuilder,
  FormGroup,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from "@angular/forms";
import { MatButtonModule } from "@angular/material/button";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatIconModule } from "@angular/material/icon";
import { MatInputModule } from "@angular/material/input";
import { MatSelectModule } from "@angular/material/select";
import { sizePattern } from "@app/regex";
import { NOT_APPLICABLE, OPTION_PACKING_SIZES } from "@models/packing-sizes";

@Component({
  selector: "fh-product-options",
  templateUrl: "./product-options.component.html",
  styleUrls: ["./product-options.component.scss"],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ProductOptionsComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => ProductOptionsComponent),
      multi: true,
    },
  ],
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatButtonModule,
    MatSelectModule,
  ],
})
export class ProductOptionsComponent implements OnInit, OnChanges, ControlValueAccessor {
  /** The bag / box sizes an option can ship in, "No Transhipment" first. */
  readonly packingSizes = OPTION_PACKING_SIZES;
  form!: FormGroup;

  @Input() price = 0;
  @Input() isRequestQuote = false;
  /**
   * The destination is the supplier country: a variation's Transhipment
   * Packing Size defaults to No Transhipment. Otherwise (or not yet known) the
   * merchant must choose one.
   */
  @Input() sameCountry: boolean | null = null;

  /** Sizes this component filled in with No Transhipment, not the merchant. */
  private defaulted = new Set<AbstractControl>();

  constructor(private fb: FormBuilder) {}

  ngOnInit(): void {
    // Initialize the form with an empty options array
    this.form = this.fb.group({
      options: this.fb.array([]),
    });

    // Add a default option if none exist
    if (this.options.length === 0) {
      this.options.push(this.createOption(this.price));
    }
    this.applyPackingDefaults();

    // Subscribe to value changes to update the parent form
    this.form.valueChanges.subscribe((value) => {
      console.log("Product options changed:", value.options);
      // Pass the options array directly to the parent form
      this.onChange((this.form.getRawValue() as any).options);
      this.onTouched();
    });

    // Subscribe to status changes for validation
    this.form.statusChanges.subscribe(() => {
      this.onValidatorChange();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.isRequestQuote && this.form) {
      this.applyRequestQuoteToOptions();
    }
    if (changes.sameCountry && this.form) {
      this.applyPackingDefaults();
      this.onChange((this.form.getRawValue() as any).options);
      this.onValidatorChange();
    }
  }

  /**
   * Same country: an unset size becomes No Transhipment. Another country: a
   * size only this component set is cleared again, so the merchant chooses.
   */
  private applyPackingDefaults(): void {
    for (const group of this.options.controls) {
      const size = group.get("packing_size");
      if (!size || group.get("state")?.value === "delete") continue;
      if (size.dirty) this.defaulted.delete(size);
      if (this.sameCountry) {
        if (!size.value) {
          size.setValue(NOT_APPLICABLE, { emitEvent: false });
          this.defaulted.add(size);
        }
      } else if (this.defaulted.has(size)) {
        size.setValue(null, { emitEvent: false });
        this.defaulted.delete(size);
      }
    }
  }

  applyRequestQuoteToOptions(): void {
    this.options?.controls.forEach((ctrl) => {
      const priceCtrl = ctrl.get("price");
      if (!priceCtrl) return;
      if (this.isRequestQuote) {
        priceCtrl.setValue(0, { emitEvent: false });
        priceCtrl.disable({ emitEvent: false });
      } else {
        priceCtrl.enable({ emitEvent: false });
      }
    });
    // Notify parent form control with updated values (including disabled price = 0)
    if (this.form) {
      this.onChange((this.form.getRawValue() as any).options);
    }
  }

  get options(): FormArray {
    return this.form.get("options") as FormArray;
  }

  // Every call site that omits `state` is creating a brand-new, never-saved
  // row (id: 0) — the initial default row in ngOnInit/writeValue, not an
  // existing one being edited. It must default to "add": the backend's
  // handleProductOptions branches purely on `state` ("add" -> INSERT,
  // "edit" -> UPDATE ... WHERE id = $n). With the old "edit" default, that
  // UPDATE ran with id = 0, matched zero rows, and silently did nothing —
  // no error, "Save successful", but the option was never created. This is
  // exactly what happened to product 30905 and 27 other products: a brand
  // new product (or one whose options list was already empty) whose only
  // option was never anything but this default row.
  createOption(price: number, state = "add"): FormGroup {
    return this.fb.group({
      id: [0],
      addon_name: ["", [Validators.required, this.duplicateNameValidator()]],
      // Above 0: an option at 0 would sell for the markup alone. Request Quote
      // disables the field, which skips this check.
      price: [price, [Validators.required, Validators.min(0.01), Validators.pattern(sizePattern)]],
      status: ["active"],
      state: [state],
      packing_size: [null as string | null, Validators.required],
    });
  }

  addOption(): void {
    this.options.push(this.createOption(0, "add"));
    this.applyPackingDefaults();
    this.applyRequestQuoteToOptions();
    // Trigger change detection to update the parent form
    this.onChange((this.form.getRawValue() as any).options);
  }

  removeOption(index: number): void {
    const control = this.options.at(index);
    if (control.get("state")?.value === "add") {
      this.options.removeAt(index);
    } else {
      control.patchValue({ state: "delete" });
    }

    // duplicateNameValidator compares each row against the others, so removing a
    // row can clear a duplicate error on the one left behind — but that row's
    // validator does not re-run on its own when a *different* row changes. Without
    // this, deleting one of two same-named options left the survivor stuck showing
    // a duplicate error, still blocking Save.
    this.options.controls.forEach((group) =>
      group.get("addon_name")?.updateValueAndValidity({ emitEvent: false })
    );
    this.onValidatorChange();

    // Trigger change detection to update the parent form
    this.onChange((this.form.getRawValue() as any).options);
  }

  writeValue(value: any): void {
    if (value && Array.isArray(value)) {
      // Clear existing options
      while (this.options.length) {
        this.options.removeAt(0);
      }

      // Add options from the value
      if (value.length > 0) {
        value.forEach((v: any) => {
          this.options.push(
            this.fb.group({
              id: [v.id || 0],
              addon_name: [
                v.addon_name || "",
                [Validators.required, this.duplicateNameValidator()],
              ],
              price: [
                v.price || 0,
                [Validators.required, Validators.min(0.01), Validators.pattern(sizePattern)],
              ],
              status: [v.status || "active"],
              state: [v.state || "edit"],
              packing_size: [v.packing_size ?? null, Validators.required],
            })
          );
        });
      } else {
        // If no options provided, add a default one
        this.options.push(this.createOption(this.price));
      }
    } else {
      // Reset to default state with one empty option
      while (this.options.length) {
        this.options.removeAt(0);
      }
      this.options.push(this.createOption(this.price));
    }

    this.applyPackingDefaults();
    // Apply disabled state if needed
    this.applyRequestQuoteToOptions();
    // Trigger change detection
    this.onChange((this.form.getRawValue() as any).options);
  }

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  onChange: any = () => {};
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  onTouched: any = () => {};
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  onValidatorChange: any = () => {};

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  registerOnValidatorChange(fn: any): void {
    this.onValidatorChange = fn;
  }

  validate(): ValidationErrors | null {
    // Only rows that will survive the save get a vote. A row marked state:"delete"
    // stays in the FormArray because the backend needs it to know what to remove,
    // but the template hides it — so while its validators still counted, a deleted
    // row with a blank name, a bad price, or a now-stale duplicate-name error held
    // the whole product form invalid and disabled Save with nothing on screen to
    // fix. (admin-backend's Zod schema had to be relaxed for the same reason.)
    if (!this.form) return null;
    const hasInvalidLiveOption = this.options.controls.some(
      (group) => group.get("state")?.value !== "delete" && group.invalid
    );
    return hasInvalidLiveOption ? { invalid: true } : null;
  }

  private duplicateNameValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const name = control.value;
      const options = (control.parent?.parent as FormArray)?.controls || [];
      const hasDuplicate = options.some(
        (group: AbstractControl) =>
          group !== control.parent &&
          group.get("addon_name")?.value === name &&
          group.get("state")?.value !== "delete"
      );
      return hasDuplicate ? { duplicateName: true } : null;
    };
  }
}
